"use client";

import { CitationTool } from "@/components/citation-tool";
import { fillSlots } from "@/components/tools/fill-slots";
import { useToolUi } from "@/components/tools/tool-ui";
import { getFeaturedStyles } from "@/data/citation-styles";

const EXAMPLES = [
  {
    style: "APA 7th edition",
    label: "Reference list",
    example:
      "Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), Title of book (pp. xx–xx). Publisher.",
    inText: "(Author, Year)",
  },
  {
    style: "MLA 9th edition",
    label: "Works Cited",
    example:
      'Author Last, First. "Title of Chapter." Title of Book, edited by First Last, Publisher, Year, pp. xx–xx.',
    inText: "(Author xx)",
  },
  {
    style: "Chicago (notes & bibliography)",
    label: "Bibliography",
    example:
      'Author Last, First. "Title of Chapter." In Title of Book, edited by First Last. Publisher, Year.',
    inText: "1. First Last, “Title of Chapter,” …",
  },
];

/** English strings. A translated content file overrides these through `ui`. */
export const BOOK_CHAPTER_CITATION_GENERATOR_STRINGS = {
  bookChapterCitationExamples: "Book Chapter Citation Examples",
  inText: "In-text: ",
  chapterVsWholeBook: "Chapter vs. Whole Book",
  chapter: "chapter",
  chapterIntro:
    "Cite the {chapter} — not the whole book — whenever you are quoting or paraphrasing a single contribution to an edited collection or anthology where each chapter has its own author:",
  chapterAuthor: "Chapter author.",
  thePersonWhoWroteThe:
    " The person who wrote the specific chapter you read — they lead the reference and appear in your in-text citation.",
  bookEditorS: "Book editor(s).",
  in: "In",
  ed: "(Ed.)",
  eds: "(Eds.)",
  editorS: "Editor(s)",
  editorNote:
    "The person who compiled the whole volume — they appear after the word {in}, followed by {ed} or {eds} in APA. Enter them in the {editors} field above.",
  bookTitlePages: "Book title & pages.",
  giveTheItalicizedBookTitle:
    "Give the italicized book title and the page range of the chapter so readers can find it inside the volume.",
  howToCiteABook: "How to Cite a Book Chapter",
  n1GatherTheChapterDetails: "1. Gather the chapter details.",
  noteTheChapterAuthorS:
    "Note the chapter author(s), the chapter title, the book's editor(s), the book title, the page range of the chapter, the publisher, and the year.",
  n2EnterThemPickA: "2. Enter them & pick a style.",
  bookChapter: "Book Chapter",
  stepEnterBody:
    "The generator above opens on the {sourceType} source type — fill in the fields, including the Editor(s) and Book title, and choose APA, MLA, Chicago, or any of the {styleCount} styles.",
  n3Cite: "3. Cite.",
  chapterAuthorChapterTitleIn:
    "Chapter Author. Chapter Title. In Editor (Ed.), Book Title (pp. x–y). Publisher.",
  stepCiteBody:
    "We build a full reference in the form {form} Edit any field, then copy or export it to BibTeX, RIS, CSV, or Word.",
} as const;

export default function BookChapterCitationGeneratorWidget() {
  const strings = useToolUi(BOOK_CHAPTER_CITATION_GENERATOR_STRINGS);

  return (
    <>
      {/* Live generator — opens on the Book Chapter (CSL chapter) manual-entry form */}
      <CitationTool
        initialManualType="chapter"
        initialMode="manual"
        styles={getFeaturedStyles()}
      />
      {/* Examples */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.bookChapterCitationExamples}
        </h2>
        <div className="space-y-4">
          {EXAMPLES.map((ex) => (
            <div
              className="rounded-lg border border-border bg-muted/30 p-4"
              key={ex.style}
            >
              <p className="mb-1 font-semibold text-foreground text-sm">
                {ex.style}
                <span className="ml-2 font-normal text-muted-foreground">
                  {ex.label}
                </span>
              </p>
              <p className="font-mono text-foreground text-sm leading-relaxed">
                {ex.example}
              </p>
              <p className="mt-2 text-muted-foreground text-sm">
                {strings.inText}
                <span className="font-mono">{ex.inText}</span>
              </p>
            </div>
          ))}
        </div>
      </section>
      {/* Chapter vs whole book explainer */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.chapterVsWholeBook}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {fillSlots(strings.chapterIntro, {
            chapter: (
              <strong className="text-foreground">{strings.chapter}</strong>
            ),
          })}
        </p>
        <ul className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <li>
            <strong className="text-foreground">{strings.chapterAuthor}</strong>
            {strings.thePersonWhoWroteThe}
          </li>
          <li>
            <strong className="text-foreground">{strings.bookEditorS}</strong>{" "}
            {fillSlots(strings.editorNote, {
              in: <span className="font-mono">{strings.in}</span>,
              ed: <span className="font-mono">{strings.ed}</span>,
              eds: <span className="font-mono">{strings.eds}</span>,
              editors: (
                <span className="font-medium text-foreground">
                  {strings.editorS}
                </span>
              ),
            })}
          </li>
          <li>
            <strong className="text-foreground">
              {strings.bookTitlePages}
            </strong>{" "}
            {strings.giveTheItalicizedBookTitle}
          </li>
        </ul>
      </section>
      {/* How to */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.howToCiteABook}
        </h2>
        <ol className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <li>
            <strong className="text-foreground">
              {strings.n1GatherTheChapterDetails}
            </strong>{" "}
            {strings.noteTheChapterAuthorS}
          </li>
          <li>
            <strong className="text-foreground">
              {strings.n2EnterThemPickA}
            </strong>{" "}
            {fillSlots(strings.stepEnterBody, {
              sourceType: (
                <span className="font-medium text-foreground">
                  {strings.bookChapter}
                </span>
              ),
            })}
          </li>
          <li>
            <strong className="text-foreground">{strings.n3Cite}</strong>{" "}
            {fillSlots(strings.stepCiteBody, {
              form: (
                <span className="font-mono">
                  {strings.chapterAuthorChapterTitleIn}
                </span>
              ),
            })}
          </li>
        </ol>
      </section>
    </>
  );
}

/**
 * English only for now. Add locales here as this page type is translated — the
 * registry in src/lib/i18n/localized-routes.ts has to agree, or the proxy will
 * redirect the URL this generates away again.
 */
export function generateStaticParams() {
  return [{ locale: "en" }];
}

export const dynamicParams = false;
